/*!
 * DIREVULF Create Your Own — customization storage (cyo-storage.js)
 *
 * The editor talks ONLY to the CustomizationStorage interface below. Today the
 * implementation is LocalCustomizationStorage: everything lives in this
 * browser's IndexedDB (design JSON, the customer's original uploads, preview
 * and print files). Nothing leaves the device, so:
 *
 *   - designs survive a reload on the same browser/device, not a new device;
 *   - DIREVULF does NOT receive the artwork or print files from this storage;
 *   - clearing site data deletes them.
 *
 * To move to a secure backend later, implement CloudCustomizationStorage
 * (contract documented on the class) and change "storage" in the studio's
 * config block (sections/cyo-studio.liquid) to { "mode": "cloud",
 * "endpoint": "https://..." }. The editor does not change.
 */
(function () {
  'use strict';

  var ALPHABET = 'ABCDEFGHJKMNPQRSTVWXYZ23456789'; // no 0/O/1/I/L/U
  function randomId(n) {
    var a = new Uint8Array(n), s = '';
    (window.crypto || window.msCrypto).getRandomValues(a);
    for (var i = 0; i < n; i++) s += ALPHABET[a[i] % ALPHABET.length];
    return s;
  }
  function newCustomizationId() { return 'DV-CUSTOM-' + randomId(8); }
  function newAssetId() { return 'A-' + Date.now().toString(36) + '-' + randomId(6); }

  function StorageError(code, message) { var e = new Error(message); e.code = code; return e; }

  /* ================================================================ interface */
  /**
   * @interface CustomizationStorage
   * kind:            'local' | 'memory' | 'cloud'
   * label:           human label for the internal status line
   * persistent:      true when a reload on this device keeps the data
   * createCustomization(meta) -> Promise<design>   new design record with a fresh id
   * getCustomization(id)      -> Promise<design|null>
   * saveDesign(design)        -> Promise<design>   full replace, stamps updatedAt
   * loadDesign(id)            -> Promise<design|null>
   * updateDesign(id, patch)   -> Promise<design>   shallow merge
   * deleteDesign(id)          -> Promise<void>     also removes its outputs
   * listDesigns()             -> Promise<Array<{id,template,updatedAt,status}>>
   * putAsset(blob, meta)      -> Promise<{id}>     customer upload (original bytes)
   * getAsset(id)              -> Promise<{id,blob,meta}|null>
   * putOutput(designId, name, blob, meta) -> Promise<{ref}>  preview / print files
   * getOutput(designId, name) -> Promise<{blob,meta}|null>
   * listOutputs(designId)     -> Promise<Array<{name,meta,size}>>
   */

  /* ================================================================ IndexedDB */
  var DB_NAME = 'dv-cyo', DB_VERSION = 1;
  function openDb() {
    return new Promise(function (res, rej) {
      if (!('indexedDB' in window)) return rej(StorageError('no_idb', 'IndexedDB unavailable'));
      var req;
      try { req = indexedDB.open(DB_NAME, DB_VERSION); } catch (e) { return rej(e); }
      req.onupgradeneeded = function () {
        var db = req.result;
        if (!db.objectStoreNames.contains('designs')) db.createObjectStore('designs', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('assets')) db.createObjectStore('assets', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('outputs')) {
          var o = db.createObjectStore('outputs', { keyPath: 'key' });
          o.createIndex('design', 'designId', { unique: false });
        }
      };
      req.onsuccess = function () { res(req.result); };
      req.onerror = function () { rej(req.error || StorageError('idb_open', 'Could not open IndexedDB')); };
      req.onblocked = function () { rej(StorageError('idb_blocked', 'IndexedDB blocked')); };
    });
  }
  function tx(db, store, mode, fn) {
    return new Promise(function (res, rej) {
      var t = db.transaction(store, mode), s = t.objectStore(store), out;
      Promise.resolve(fn(s, function (v) { out = v; })).catch(rej);
      t.oncomplete = function () { res(out); };
      t.onerror = function () { rej(t.error); };
      t.onabort = function () { rej(t.error || StorageError('idb_abort', 'Transaction aborted')); };
    });
  }
  function reqP(r) { return new Promise(function (res, rej) { r.onsuccess = function () { res(r.result); }; r.onerror = function () { rej(r.error); }; }); }

  /* small metadata index in localStorage (never images) */
  var LS_INDEX = 'dv-cyo:index', LS_LAST = 'dv-cyo:last:';
  function lsGet(k, d) { try { var v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch (e) { return d; } }
  function lsSet(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* quota or disabled: metadata only, safe to skip */ } }
  function touchIndex(d) {
    var idx = lsGet(LS_INDEX, []).filter(function (x) { return x.id !== d.id; });
    idx.unshift({ id: d.id, template: d.template, updatedAt: d.updatedAt, status: d.status || 'draft' });
    lsSet(LS_INDEX, idx.slice(0, 30));
    lsSet(LS_LAST + d.template, d.id);
  }
  function dropIndex(id) { lsSet(LS_INDEX, lsGet(LS_INDEX, []).filter(function (x) { return x.id !== id; })); }

  function now() { return new Date().toISOString(); }
  function baseDesign(meta) {
    var t = now();
    return Object.assign({
      id: newCustomizationId(), version: 1, status: 'draft', createdAt: t, updatedAt: t,
      template: null, productId: null, variantId: null, options: {}, surfaces: {}, meta: {}, outputs: {}, storage: null
    }, meta || {});
  }

  /* ================================================================ Local (IndexedDB) */
  function LocalCustomizationStorage() {
    this.kind = 'local';
    this.label = 'LOCAL / PROTOTYPE';
    this.persistent = true;
    this._db = null;
  }
  LocalCustomizationStorage.prototype._open = function () {
    var self = this;
    if (self._db) return Promise.resolve(self._db);
    return openDb().then(function (db) { self._db = db; return db; });
  };
  LocalCustomizationStorage.prototype.createCustomization = function (meta) {
    var d = baseDesign(meta); d.storage = this.kind;
    return this.saveDesign(d);
  };
  LocalCustomizationStorage.prototype.getCustomization = function (id) { return this.loadDesign(id); };
  LocalCustomizationStorage.prototype.saveDesign = function (design) {
    design.updatedAt = now();
    var copy = JSON.parse(JSON.stringify(design));
    return this._open().then(function (db) { return tx(db, 'designs', 'readwrite', function (s) { s.put(copy); }); })
      .then(function () { touchIndex(copy); return design; });
  };
  LocalCustomizationStorage.prototype.loadDesign = function (id) {
    if (!id) return Promise.resolve(null);
    return this._open().then(function (db) { return tx(db, 'designs', 'readonly', function (s, set) { return reqP(s.get(id)).then(set); }); })
      .then(function (d) { return d || null; });
  };
  LocalCustomizationStorage.prototype.updateDesign = function (id, patch) {
    var self = this;
    return self.loadDesign(id).then(function (d) {
      if (!d) throw StorageError('not_found', 'No design ' + id);
      return self.saveDesign(Object.assign(d, patch || {}));
    });
  };
  LocalCustomizationStorage.prototype.deleteDesign = function (id) {
    var self = this;
    return self._open().then(function (db) {
      return tx(db, 'designs', 'readwrite', function (s) { s.delete(id); }).then(function () {
        return tx(db, 'outputs', 'readwrite', function (s) {
          return reqP(s.index('design').getAllKeys(id)).then(function (keys) { keys.forEach(function (k) { s.delete(k); }); });
        });
      });
    }).then(function () { dropIndex(id); });
  };
  LocalCustomizationStorage.prototype.listDesigns = function () { return Promise.resolve(lsGet(LS_INDEX, [])); };
  LocalCustomizationStorage.prototype.lastDesignId = function (template) { return lsGet(LS_LAST + template, null); };
  LocalCustomizationStorage.prototype.putAsset = function (blob, meta) {
    var rec = { id: newAssetId(), blob: blob, meta: meta || {}, createdAt: now() };
    return this._open().then(function (db) { return tx(db, 'assets', 'readwrite', function (s) { s.put(rec); }); }).then(function () { return { id: rec.id }; });
  };
  LocalCustomizationStorage.prototype.getAsset = function (id) {
    return this._open().then(function (db) { return tx(db, 'assets', 'readonly', function (s, set) { return reqP(s.get(id)).then(set); }); })
      .then(function (r) { return r || null; });
  };
  LocalCustomizationStorage.prototype.putOutput = function (designId, name, blob, meta) {
    var rec = { key: designId + ':' + name, designId: designId, name: name, blob: blob, meta: meta || {}, size: blob.size, createdAt: now() };
    return this._open().then(function (db) { return tx(db, 'outputs', 'readwrite', function (s) { s.put(rec); }); })
      .then(function () { return { ref: 'idb:outputs/' + rec.key }; });
  };
  LocalCustomizationStorage.prototype.getOutput = function (designId, name) {
    return this._open().then(function (db) { return tx(db, 'outputs', 'readonly', function (s, set) { return reqP(s.get(designId + ':' + name)).then(set); }); })
      .then(function (r) { return r ? { blob: r.blob, meta: r.meta } : null; });
  };
  LocalCustomizationStorage.prototype.listOutputs = function (designId) {
    return this._open().then(function (db) {
      return tx(db, 'outputs', 'readonly', function (s, set) { return reqP(s.index('design').getAll(designId)).then(set); });
    }).then(function (rows) { return (rows || []).map(function (r) { return { name: r.name, meta: r.meta, size: r.size }; }); });
  };

  /* ================================================================ Memory (IndexedDB unavailable) */
  function MemoryCustomizationStorage() {
    this.kind = 'memory';
    this.label = 'MEMORY / PROTOTYPE (lost on reload)';
    this.persistent = false;
    this.d = new Map(); this.a = new Map(); this.o = new Map();
  }
  MemoryCustomizationStorage.prototype.createCustomization = function (meta) { var d = baseDesign(meta); d.storage = this.kind; return this.saveDesign(d); };
  MemoryCustomizationStorage.prototype.getCustomization = function (id) { return this.loadDesign(id); };
  MemoryCustomizationStorage.prototype.saveDesign = function (d) { d.updatedAt = now(); this.d.set(d.id, JSON.parse(JSON.stringify(d))); return Promise.resolve(d); };
  MemoryCustomizationStorage.prototype.loadDesign = function (id) { var d = this.d.get(id); return Promise.resolve(d ? JSON.parse(JSON.stringify(d)) : null); };
  MemoryCustomizationStorage.prototype.updateDesign = function (id, p) { var self = this; return self.loadDesign(id).then(function (d) { if (!d) throw StorageError('not_found', id); return self.saveDesign(Object.assign(d, p)); }); };
  MemoryCustomizationStorage.prototype.deleteDesign = function (id) { this.d.delete(id); var self = this; Array.from(this.o.keys()).forEach(function (k) { if (k.indexOf(id + ':') === 0) self.o.delete(k); }); return Promise.resolve(); };
  MemoryCustomizationStorage.prototype.listDesigns = function () { return Promise.resolve(Array.from(this.d.values()).map(function (d) { return { id: d.id, template: d.template, updatedAt: d.updatedAt, status: d.status }; })); };
  MemoryCustomizationStorage.prototype.lastDesignId = function () { return null; };
  MemoryCustomizationStorage.prototype.putAsset = function (blob, meta) { var id = newAssetId(); this.a.set(id, { id: id, blob: blob, meta: meta || {} }); return Promise.resolve({ id: id }); };
  MemoryCustomizationStorage.prototype.getAsset = function (id) { return Promise.resolve(this.a.get(id) || null); };
  MemoryCustomizationStorage.prototype.putOutput = function (designId, name, blob, meta) { this.o.set(designId + ':' + name, { blob: blob, meta: meta || {}, size: blob.size, name: name }); return Promise.resolve({ ref: 'memory:' + designId + ':' + name }); };
  MemoryCustomizationStorage.prototype.getOutput = function (designId, name) { var r = this.o.get(designId + ':' + name); return Promise.resolve(r ? { blob: r.blob, meta: r.meta } : null); };
  MemoryCustomizationStorage.prototype.listOutputs = function (designId) { var out = []; this.o.forEach(function (v, k) { if (k.indexOf(designId + ':') === 0) out.push({ name: v.name, meta: v.meta, size: v.size }); }); return Promise.resolve(out); };

  /* ================================================================ Cloud (NOT IMPLEMENTED — contract only) */
  /**
   * CloudCustomizationStorage — the adapter to write when a secure backend
   * exists (Cloudflare Worker, Shopify App Proxy, or any HTTPS service).
   * It must never hold credentials in the browser: the endpoint authenticates
   * the shop server-side (App Proxy HMAC, or an allow-listed Origin plus
   * per-design capability tokens it issues).
   *
   * Expected REST contract (all JSON unless noted):
   *   POST   {endpoint}/customizations                 -> 201 { id, token }        (create; server issues DV-CUSTOM id)
   *   GET    {endpoint}/customizations/:id             -> 200 design               (Authorization: Bearer token)
   *   PUT    {endpoint}/customizations/:id             -> 200 design               (full replace)
   *   PATCH  {endpoint}/customizations/:id             -> 200 design
   *   DELETE {endpoint}/customizations/:id             -> 204
   *   POST   {endpoint}/customizations/:id/assets      -> 200 { id, uploadUrl }    (then PUT bytes to the signed uploadUrl)
   *   GET    {endpoint}/assets/:id                     -> 302 to a short-lived signed URL
   *   POST   {endpoint}/customizations/:id/outputs/:name -> 200 { uploadUrl, ref } (preview.jpg / print-<surface>.png)
   *   GET    {endpoint}/customizations/:id/outputs     -> 200 [{ name, size, meta }]
   * The server re-validates files (magic bytes, size, dimensions), recomputes
   * print files if it does not trust the client ones, and on order creation
   * (orders/create webhook) links the order to the customization id found in
   * the line item properties.
   */
  function CloudCustomizationStorage(endpoint) {
    this.kind = 'cloud';
    this.label = 'CLOUD';
    this.persistent = true;
    this.endpoint = endpoint;
  }
  ['createCustomization', 'getCustomization', 'saveDesign', 'loadDesign', 'updateDesign', 'deleteDesign', 'listDesigns', 'putAsset', 'getAsset', 'putOutput', 'getOutput', 'listOutputs']
    .forEach(function (m) {
      CloudCustomizationStorage.prototype[m] = function () {
        return Promise.reject(StorageError('cloud_not_configured', 'CloudCustomizationStorage.' + m + ' is not implemented yet — no secure backend is connected.'));
      };
    });
  CloudCustomizationStorage.prototype.lastDesignId = function () { return null; };

  /* ================================================================ factory */
  function create(cfg) {
    cfg = cfg || {};
    if (cfg.mode === 'cloud' && cfg.endpoint) {
      // Deliberately refuses until implemented: the editor falls back to local
      // and reports the problem on the internal status line.
      return Promise.resolve({ storage: new LocalCustomizationStorage(), note: 'Cloud storage selected but not implemented — using local prototype storage.' })
        .then(function (r) { return r.storage._open().then(function () { return r; }); })
        .catch(function () { return { storage: new MemoryCustomizationStorage(), note: 'IndexedDB unavailable — designs are kept in memory only.' }; });
    }
    var local = new LocalCustomizationStorage();
    return local._open().then(function () { return { storage: local, note: '' }; })
      .catch(function () { return { storage: new MemoryCustomizationStorage(), note: 'IndexedDB unavailable — designs are kept in memory only.' }; });
  }

  /* ================================================================ optional Google Apps Script sync */
  /**
   * OPTIONAL and OFF by default. Posts a small JSON record (and, only if the
   * merchant ticks the setting, the preview/print files as base64) to a Google
   * Apps Script web app the merchant deploys themselves. The web-app URL is
   * public by nature: anyone who reads the page source can post to it. The
   * optional token only deters casual misuse; it is NOT a secret and this is
   * NOT secure storage. Files land in the merchant's own Drive folder, private
   * to that Google account unless the merchant shares them.
   */
  function blobToBase64(blob) {
    return new Promise(function (res, rej) {
      var r = new FileReader();
      r.onload = function () { res(String(r.result).split(',')[1] || ''); };
      r.onerror = function () { rej(r.error); };
      r.readAsDataURL(blob);
    });
  }
  function sheetSync(cfg, record, files) {
    if (!cfg || !cfg.enabled || !/^https:\/\/script\.google\.com\/macros\/s\/[\w-]+\/exec$/.test(cfg.endpoint || '')) {
      return Promise.resolve({ ok: false, skipped: true });
    }
    var list = (cfg.sendFiles && files) ? files : [];
    return Promise.all(list.map(function (f) {
      return blobToBase64(f.blob).then(function (b64) { return { name: f.name, mime: f.blob.type, data: b64 }; });
    })).then(function (encoded) {
      var body = JSON.stringify({ token: cfg.token || '', record: record, files: encoded });
      if (body.length > 45 * 1024 * 1024) body = JSON.stringify({ token: cfg.token || '', record: record, files: [], note: 'files omitted: payload too large' });
      return fetch(cfg.endpoint, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: body, redirect: 'follow' })
        .then(function (r) { return r.ok ? r.json().catch(function () { return { ok: true }; }) : { ok: false, status: r.status }; });
    }).catch(function (e) { return { ok: false, error: String(e && e.message || e) }; });
  }

  window.DVCyoStorage = {
    create: create,
    newCustomizationId: newCustomizationId,
    LocalCustomizationStorage: LocalCustomizationStorage,
    MemoryCustomizationStorage: MemoryCustomizationStorage,
    CloudCustomizationStorage: CloudCustomizationStorage,
    sheetSync: sheetSync
  };
})();